import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faCheckToSlot,
  faCircleChevronRight,
  faCirclePause,
  faDisplay,
  faFlag,
} from '@fortawesome/pro-regular-svg-icons';

import type {
  HandsfreeOnboardingStep,
  OnboardingCardPlacement,
} from './project-usability-handsfree-onboarding.consts';

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CardPosition {
  top: number;
  left: number;
}

export function unionRect(
  a: { top: number; left: number; width: number; height: number },
  b: { top: number; left: number; width: number; height: number }
): TargetRect {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.left + a.width, b.left + b.width);
  const bottom = Math.max(a.top + a.height, b.top + b.height);
  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  };
}

export function withPadding(rect: TargetRect, padding: number): TargetRect {
  return {
    left: rect.left - padding,
    top: rect.top - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

export function getCardPosition(
  targetRect: TargetRect,
  cardSize: { width: number; height: number },
  placement: OnboardingCardPlacement,
  gap: number
): CardPosition {
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 1024;
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : 768;
  const { width: cw, height: ch } = cardSize;
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'left':
      left = targetRect.left - cw - gap;
      top = targetRect.top + targetRect.height / 2 - ch / 2;
      break;
    case 'right':
      left = targetRect.left + targetRect.width + gap;
      top = targetRect.top + targetRect.height / 2 - ch / 2;
      break;
    case 'top':
      left = targetRect.left + targetRect.width / 2 - cw / 2;
      top = targetRect.top - ch - gap;
      break;
    case 'bottom':
      left = targetRect.left + targetRect.width / 2 - cw / 2;
      top = targetRect.top + targetRect.height + gap;
      break;
  }

  left = Math.max(gap, Math.min(left, viewportWidth - cw - gap));
  top = Math.max(gap, Math.min(top, viewportHeight - ch - gap));
  return { top, left };
}

export function getStepIcon(
  icon: HandsfreeOnboardingStep['icon']
): IconDefinition {
  switch (icon) {
    case 'check':
      return faCheckToSlot;
    case 'pause':
      return faCirclePause;
    case 'arrow':
      return faCircleChevronRight;
    case 'monitor':
      return faDisplay;
    case 'flag':
      return faFlag;
    default:
      return faCheck;
  }
}
